import classNames from "classnames";
import HomeProfileCard from "../../components/home/HomeProfileCard";
import CiroContainer from "../../components/shared/CiroContainer";
import { useEffect, useState } from "react";
import { CreateIntegrationButton_UserAccount } from "../../components/manageIntegration/manageIntegrationButton/ManageIntegrationsButton";
import { gql, useQuery } from "@apollo/client";
import SkeletonLoading from "../../components/shared/SkeletonLoading";
import EnrichedContactsTable from "../../components/home/EnrichedContactsTable";
import { Home_PhoneNumbersOverviewQuery } from "../../__generated__/graphql";
import { useFragment as getFragmentData } from "../../__generated__";
import HomeGetStarted, {
  HomeGetStarted_Permissions,
} from "../../components/home/HomeGetStarted";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import ContactListTable, {
  ContactListTable_ContactList,
} from "../../components/automation/ContactListTable";
import AutomationHomeTableSection from "../../components/automation/AutomationHomeTableSection";
import UpsertContactListModal from "../../components/automation/UpsertContactListModal";
import AutomationListImportContactsModal from "../../components/automationList/AutomationListHomeComponents/AutomationListImportContactsModal";

export const Home_PhoneNumbersOverview = gql`
  query Home_PhoneNumbersOverview {
    userAccount {
      pk
      ...CreateIntegrationButton_UserAccount
      autopilotSessionsCount
    }
    organization {
      ...HomeGetStarted_Permissions
      id
    }
    permissions {
      canViewEnrichments
      canViewCiroInternalSearch
    }
    phoneNumbersRecoveredCount(requestType: batch)
    manuallyCreatedContactListsCount
  }
  ${HomeGetStarted_Permissions}
  ${CreateIntegrationButton_UserAccount}
`;

export const Home_ContactLists = gql`
  query Home_ContactLists {
    contactLists {
      ...ContactListTable_ContactList
    }
  }
  ${ContactListTable_ContactList}
`;

const Home = () => {
  const { data, loading } = useQuery<Home_PhoneNumbersOverviewQuery>(
    Home_PhoneNumbersOverview,
  );
  const {
    data: contactListsData,
    loading: loadingContactLists,
    refetch: refetchContactLists,
  } = useQuery(Home_ContactLists);
  const contactLists = contactListsData?.contactLists;
  const [isCreateListModalOpen, setIsCreateListModalOpen] = useState(false);
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const hasRecoveredPhoneNumbers = Boolean(
    data?.phoneNumbersRecoveredCount && data.phoneNumbersRecoveredCount > 0,
  );

  const hasAutopilotSessions = Boolean(
    data?.userAccount?.autopilotSessionsCount &&
      data.userAccount.autopilotSessionsCount > 0,
  );

  useEffect(() => {
    if (loading || !location?.state?.invitedToOrgId || !data?.organization?.id)
      return;
    if (Number(location.state.invitedToOrgId) !== data.organization.id) {
      toast.error(
        `Failed to accept invitation, please reach out to Ciro support for assistance.`,
      );
      console.error(
        `Invited organization ID ${location.state.invitedToOrgId} does not match current organization ID ${data.organization.id}.`,
      );
    }
  }, [loading, location?.state?.invitedToOrgId, data?.organization?.id]);

  return (
    <CiroContainer className="bg-neutral-100">
      {!loading ? (
        <>
          <HomeProfileCard />
          <HomeGetStarted
            userAccountData={data?.userAccount}
            hasAutopilotSessions={hasAutopilotSessions}
            hasRecoveredPhoneNumbers={hasRecoveredPhoneNumbers}
            canConnectCRMData={
              getFragmentData(HomeGetStarted_Permissions, data?.organization) ??
              null
            }
            showOldOnboardingExperience={
              !Boolean(data?.permissions?.canViewCiroInternalSearch)
            }
          />
          {!loadingContactLists && (
            <div className={classNames("w-full", "mt-5", "px-5")}>
              <AutomationHomeTableSection
                title="My contact lists"
                objectType="list"
                noObjectsMessage="No lists found. Lists are saved collections of contacts you want to come back to."
                table={
                  contactLists && contactLists.length > 0 ? (
                    <ContactListTable
                      contactLists={contactLists}
                      refetch={refetchContactLists}
                    />
                  ) : undefined
                }
                buttonHandler={() => setIsCreateListModalOpen(true)}
              />
            </div>
          )}

          <EnrichedContactsTable itemsPerPage={5} />
        </>
      ) : (
        <div className={classNames("w-full")}>
          <SkeletonLoading numSkeletons={1} skeletonHeight={"7rem"} />
          <div
            className={classNames(
              "flex",
              "flex-row",
              "justify-between",
              "gap-4",
              "mt-4",
              "w-full",
            )}
          >
            <div className={classNames("w-full")}>
              <SkeletonLoading numSkeletons={1} skeletonHeight={"6rem"} />
            </div>
            <div className={classNames("w-full")}>
              <SkeletonLoading numSkeletons={1} skeletonHeight={"6rem"} />
            </div>
            <div className={classNames("w-full")}>
              <SkeletonLoading numSkeletons={1} skeletonHeight={"6rem"} />
            </div>
          </div>
          <SkeletonLoading numSkeletons={1} skeletonHeight={"7rem"} />
          <div className={classNames("w-full", "mt-5")}>
            <SkeletonLoading numSkeletons={5} skeletonHeight={"2rem"} />
          </div>
        </div>
      )}
      <UpsertContactListModal
        isOpen={isCreateListModalOpen}
        onClose={() => setIsCreateListModalOpen(false)}
        onSuccess={async (contactListId) => {
          await refetchContactLists();
          setSelectedListId(contactListId);
          setIsCreateListModalOpen(false);
        }}
      />
      {selectedListId && (
        <AutomationListImportContactsModal
          isOpen={true}
          onClose={() => {
            setSelectedListId(null);
            navigate(`/autopilot/lists/${selectedListId}`);
          }}
          contactListId={selectedListId}
          onSuccess={() => {
            setSelectedListId(null);
            navigate(`/autopilot/lists/${selectedListId}`);
          }}
        />
      )}
    </CiroContainer>
  );
};

export default Home;
